import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Collapse, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { IoChevronDownOutline, IoChevronUpOutline, IoPersonOutline, IoLockClosedOutline } from 'react-icons/io5';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { getAllUsers, getAllLenders, getUserLenderAccess, updateUserLenderAccess, getAllUsersLenderAccess } from '../api/userSettingsApi';

const UserSettings = () => {
  console.log('UserSettings component rendering...');
  
  const { user } = useAuth();
  const { success, error: notifyError } = useNotifications();
  
  // State management
  const [users, setUsers] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [userLenderAccess, setUserLenderAccess] = useState({});
  const [savingUsers, setSavingUsers] = useState(new Set());

  // Function to preload lender access for all users using BULK endpoint
  const preloadAllUserAccess = async (users, lenders) => {
    try {
      console.log(`Preloading access data for ${users.length} users using BULK endpoint...`);
      
      // Fetch ALL user access in ONE API call
      const bulkAccessResponse = await getAllUsersLenderAccess();
      const userAccessMap = bulkAccessResponse.userAccessMap || {};
      
      console.log(`Received bulk access data for ${Object.keys(userAccessMap).length} users with specific settings`);
      
      // Build the complete userLenderAccess object
      const preloadedAccess = {};
      
      users.forEach(userData => {
        const userEmail = userData.email;
        const accessMap = {};
        
        // Check if this user has specific access settings
        if (userAccessMap[userEmail] && !userAccessMap[userEmail].allEnabled) {
          // User has specific settings - use them
          const serverAccessMap = userAccessMap[userEmail].accessMap;
          
          lenders.forEach(lender => {
            const serverAccess = serverAccessMap[lender.stringId];
            accessMap[lender.stringId] = {
              lenderId: lender.stringId,
              lenderName: lender.lenderName,
              enabled: serverAccess ? serverAccess.hasAccess : true // Default to enabled if not specified
            };
          });
        } else {
          // User has no specific settings - default to all enabled
          lenders.forEach(lender => {
            accessMap[lender.stringId] = {
              lenderId: lender.stringId,
              lenderName: lender.lenderName,
              enabled: true
            };
          });
        }
        
        preloadedAccess[userEmail] = accessMap;
      });
      
      console.log('Preloaded access data for all users:', preloadedAccess);
      
      // Log summary of counts for verification
      Object.entries(preloadedAccess).forEach(([userEmail, accessMap]) => {
        const enabled = Object.values(accessMap).filter(access => access.enabled).length;
        const disabled = Object.values(accessMap).filter(access => !access.enabled).length;
        console.log(`${userEmail}: ${enabled} enabled, ${disabled} disabled`);
      });
      
      setUserLenderAccess(preloadedAccess);
      
    } catch (err) {
      console.error('Error preloading user access data:', err);
      // Fall back to empty access (all enabled by default)
      const fallbackAccess = {};
      users.forEach(userData => {
        const accessMap = {};
        lenders.forEach(lender => {
          accessMap[lender.stringId] = {
            lenderId: lender.stringId,
            lenderName: lender.lenderName,
            enabled: true
          };
        });
        fallbackAccess[userData.email] = accessMap;
      });
      setUserLenderAccess(fallbackAccess);
    }
  };

  // Load initial data
  useEffect(() => {
    console.log('UserSettings useEffect running - loading initial data...');
    
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Loading users and lenders...');
        
        // Load users and lenders in parallel
        const [usersResponse, lendersResponse] = await Promise.all([
          getAllUsers(),
          getAllLenders()
        ]);

        console.log('Users loaded:', usersResponse);
        console.log('Lenders loaded:', lendersResponse);

        const loadedUsers = usersResponse.users || [];
        const loadedLenders = lendersResponse.lenders || [];
        
        setUsers(loadedUsers);
        setLenders(loadedLenders);
        
        // Preload lender access for all users to show accurate counts
        console.log('Preloading lender access for all users...');
        await preloadAllUserAccess(loadedUsers, loadedLenders);
      } catch (err) {
        console.error('Error loading data:', err);
        const errorMessage = 'Failed to load users and lenders. Please try again.';
        setError(errorMessage);
        // Use a timeout to prevent cascade re-renders with notification
        setTimeout(() => notifyError('Failed to load user settings data'), 0);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []); // Remove notifyError dependency - notification functions are stable

  // Toggle user expansion (data is already preloaded)
  const toggleUserExpansion = (userEmail) => {
    const newExpanded = new Set(expandedUsers);
    
    if (newExpanded.has(userEmail)) {
      newExpanded.delete(userEmail);
    } else {
      newExpanded.add(userEmail);
    }
    
    setExpandedUsers(newExpanded);
  };

  // Toggle lender access for a user
  const toggleLenderAccess = (userEmail, lenderId) => {
    setUserLenderAccess(prev => ({
      ...prev,
      [userEmail]: {
        ...prev[userEmail],
        [lenderId]: {
          ...prev[userEmail][lenderId],
          enabled: !prev[userEmail][lenderId].enabled
        }
      }
    }));
  };

  // Save lender access settings for a user
  const saveLenderAccess = async (userEmail) => {
    try {
      console.log(`Starting save for ${userEmail}...`);
      setSavingUsers(prev => new Set([...prev, userEmail]));
      
      // Convert access map to array format for API
      const accessArray = Object.values(userLenderAccess[userEmail] || {});
      
      console.log(`Saving lender access for ${userEmail}:`, accessArray);
      
      await updateUserLenderAccess(userEmail, accessArray);
      
      console.log(`Save completed for ${userEmail}`);
      
      // Use timeout to prevent cascade re-renders
      setTimeout(() => {
        success(`Lender access updated for ${userEmail}`, 'Settings Saved');
      }, 0);
    } catch (err) {
      console.error(`Error saving lender access for ${userEmail}:`, err);
      // Use timeout to prevent cascade re-renders
      setTimeout(() => {
        notifyError(`Failed to save settings for ${userEmail}`);
      }, 0);
    } finally {
      setSavingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userEmail);
        return newSet;
      });
    }
  };

  // Get role badge variant
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'developer': return 'danger';
      case 'super_admin': return 'warning';
      case 'admin': return 'info';
      default: return 'secondary';
    }
  };

  // Count enabled/disabled lenders for a user
  const getLenderCounts = (userEmail) => {
    const accessMap = userLenderAccess[userEmail];
    if (!accessMap) return { enabled: lenders.length, disabled: 0 };
    
    const enabled = Object.values(accessMap).filter(access => access.enabled).length;
    const disabled = Object.values(accessMap).filter(access => !access.enabled).length;
    
    return { enabled, disabled };
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading users, lenders, and access settings...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h5>Error Loading User Settings</h5>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row>
        <Col>
          <div className="d-flex align-items-center mb-4">
            <IoLockClosedOutline size={24} className="me-2" />
            <h2>User Settings</h2>
            <Badge bg="danger" className="ms-2">Developer Only</Badge>
          </div>
          
          <Alert variant="info">
            <strong>Lender Access Control:</strong> Manage which lenders each user can submit deals to. 
            All lenders are enabled by default. Toggle individual lenders off to restrict access.
          </Alert>

          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <IoPersonOutline className="me-2" />
                Users ({users.length})
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              {users.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No users found</p>
                </div>
              ) : (
                <Table responsive className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Lender Access</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userData) => {
                      const isExpanded = expandedUsers.has(userData.email);
                      const isSaving = savingUsers.has(userData.email);
                      const { enabled, disabled } = getLenderCounts(userData.email);
                      
                      return (
                        <React.Fragment key={userData.email}>
                          <tr>
                            <td>
                              <div className="d-flex align-items-center">
                                <strong>{userData.email}</strong>
                              </div>
                            </td>
                            <td>
                              <Badge bg={getRoleBadgeVariant(userData.role)}>
                                {userData.role}
                              </Badge>
                            </td>
                            <td>
                              <span className="text-success me-2">{enabled} enabled</span>
                              {disabled > 0 && (
                                <span className="text-danger">{disabled} disabled</span>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => toggleUserExpansion(userData.email)}
                              >
                                {isExpanded ? (
                                  <>Hide Lenders <IoChevronUpOutline /></>
                                ) : (
                                  <>Manage Lenders <IoChevronDownOutline /></>
                                )}
                              </Button>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="4" className="p-0">
                              <Collapse in={isExpanded}>
                                <div className="p-3 bg-light border-top">
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6>Lender Access for {userData.email}</h6>
                                    <Button
                                      variant="success"
                                      size="sm"
                                      onClick={() => saveLenderAccess(userData.email)}
                                      disabled={isSaving}
                                    >
                                      {isSaving ? (
                                        <>
                                          <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            className="me-2"
                                          />
                                          Saving...
                                        </>
                                      ) : (
                                        'Save Changes'
                                      )}
                                    </Button>
                                  </div>
                                  
                                  <Row>
                                    {lenders.map((lender) => {
                                      const accessSetting = userLenderAccess[userData.email]?.[lender.stringId];
                                      const isEnabled = accessSetting ? accessSetting.enabled : true;
                                      
                                      return (
                                        <Col md={6} lg={4} key={lender.stringId} className="mb-2">
                                          <Form.Check
                                            type="switch"
                                            id={`${userData.email}-${lender.stringId}`}
                                            label={
                                              <div>
                                                <strong>{lender.lenderName}</strong>
                                                <br />
                                                <small className="text-muted">
                                                  {lender.financingType} • Tier {lender.tier}
                                                </small>
                                              </div>
                                            }
                                            checked={isEnabled}
                                            onChange={() => toggleLenderAccess(userData.email, lender.stringId)}
                                          />
                                        </Col>
                                      );
                                    })}
                                  </Row>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserSettings; 